// Auth0 configuration for Trucks Parking Hub
import logger from '../utils/logger';

// Auth0 configuration
export const auth0Config = {
  domain: process.env.REACT_APP_AUTH0_DOMAIN,
  clientId: process.env.REACT_APP_AUTH0_CLIENT_ID,
  audience: process.env.REACT_APP_AUTH0_AUDIENCE,
  redirectUri: `${window.location.origin}/callback`,
  scope: 'openid profile email read:admin write:admin offline_access'
};

// Validate required environment variables
if (!auth0Config.domain || !auth0Config.clientId || !auth0Config.audience) {
  logger.error('Missing required Auth0 environment variables', {
    component: 'Auth0Config',
    REACT_APP_AUTH0_DOMAIN: !!process.env.REACT_APP_AUTH0_DOMAIN ? 'Set' : 'Missing',
    REACT_APP_AUTH0_CLIENT_ID: !!process.env.REACT_APP_AUTH0_CLIENT_ID ? 'Set' : 'Missing',
    REACT_APP_AUTH0_AUDIENCE: !!process.env.REACT_APP_AUTH0_AUDIENCE ? 'Set' : 'Missing'
  });
  throw new Error('Missing required Auth0 environment variables. Please check your .env file.');
}

logger.debug('Auth0 configuration loaded', {
  component: 'Auth0Config',
  domain: auth0Config.domain,
  clientId: auth0Config.clientId?.substring(0, 8) + '...',
  audience: auth0Config.audience,
  redirectUri: auth0Config.redirectUri
});

// Auth0 roles
export const AUTH0_ROLES = {
  ADMIN: 'admin',
  SUPERADMIN: 'superadmin',
  CUSTOMER: 'customer',
  PREMIUM_CUSTOMER: 'premium_customer',
  FLEET_MANAGER: 'fleet_manager'
};

// Debug function to decode JWT token (JWE tokens cannot be decoded client-side)
export const debugToken = (token) => {
  if (!token) {
    logger.debug('No token provided', { component: 'debugToken' });
    return null;
  }
  
  const tokenParts = token.split('.');
  const tokenType = tokenParts.length === 3 ? 'JWT' : tokenParts.length === 5 ? 'JWE' : 'Unknown';
  
  logger.debug('Token analysis', {
    component: 'debugToken',
    parts: tokenParts.length,
    type: tokenType
  });
  
  if (tokenParts.length === 5) {
    logger.debug('JWE token detected - cannot decode client-side', { component: 'debugToken' });
    return { type: 'JWE', encrypted: true, message: 'Cannot decode JWE client-side' };
  }
  
  if (tokenParts.length !== 3) {
    logger.debug('Invalid token format', { component: 'debugToken' });
    return null;
  }
  
  try {
    const base64Url = tokenParts[1];
    if (!base64Url) {
      logger.debug('Empty payload section', { component: 'debugToken' });
      return null;
    }
    
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    
    const decoded = JSON.parse(jsonPayload);
    logger.debug('Decoded JWT payload', { component: 'debugToken', decoded });
    return decoded;
  } catch (error) {
    logger.error('Error decoding token', { component: 'debugToken', error: error.message });
    return null;
  }
};

// Check if user has admin role
export const isAdmin = (user) => {
  if (!user) return false;
  
  const roles = user['https://trucksparkinghub.com/roles'] || [];
  const hasAdminRole = roles.includes('admin') || roles.includes('superadmin');
  
  logger.debug('Admin role check', {
    component: 'isAdmin',
    email: user.email,
    hasAdminRole
  });
  
  return hasAdminRole;
};

// Check if user has customer role
export const isCustomer = (user) => {
  if (!user) {
    return false;
  }
  
  const roles = user['https://trucksparkinghub.com/roles'] || [];
  return roles.includes('customer') || roles.includes('premium_customer') || roles.includes('fleet_manager');
};

// Get user role (returns the first role found)
export const getUserRole = (user) => {
  if (!user) return null;
  
  const roles = user['https://trucksparkinghub.com/roles'] || [];
  return roles.length > 0 ? roles[0] : null;
}; 